import {
  areaName,
  ASSET_STATUS_LABELS,
  assetFields,
  assetTypeName,
  hasPorts,
  PORT_TYPE_LABELS,
  subject,
  type AssetDetail,
  type AssetHistoryEntry,
  type AssetStatus,
  type PortType,
  type PortView,
} from '@ftthnms/shared';
import { Badge, Button, RegionChip, type BadgeTone } from '@ftthnms/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  Crosshair,
  Link2,
  Move,
  Pencil,
  RotateCcw,
  Route as RouteIcon,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { AssetGlyph } from '../map/AssetGlyph';
import { dateTime, metres, nf, nf1, rupiah, send } from '../lib/api';
import { invalidateAssets, useAsset, useHistory, usePorts, useTrace } from '../lib/assets';
import { useSession } from '../lib/session';
import { DeleteAssetDialog } from './DeleteAssetDialog';
import { Drawer } from './Drawer';
import { MonitoringPanel } from './MonitoringPanel';
import { MONITORABLE_TYPES } from '../lib/monitoring';
import { ConnectDialog, PortPanel } from './PortPanel';
import { useToast } from './Toast';

export const STATUS_TONE: Record<string, BadgeTone> = {
  active: 'success',
  planned: 'warning',
  maintenance: 'danger',
  inactive: 'neutral',
  decommissioned: 'neutral',
};

type Tab = 'summary' | 'ports' | 'trace' | 'monitoring' | 'history';
const TRACE_TYPES = ['ODC', 'ODP', 'CPE'];

/** Asset detail (AST-03) in a drawer: summary, ports, customer trace and change history. */
export function AssetDrawer({
  id,
  onClose,
  onEdit,
  onOpenAsset,
  onMove,
  onStartRoute,
  onFocus,
  onTrace,
  onDeleted,
  initialTab = 'summary',
}: {
  id: string;
  initialTab?: Tab;
  onClose: () => void;
  onEdit: (a: AssetDetail) => void;
  onOpenAsset: (id: string) => void;
  onMove?: (a: AssetDetail) => void;
  onStartRoute?: (a: AssetDetail) => void;
  onFocus?: (a: AssetDetail) => void;
  onTrace?: (coords: Array<[number, number]> | null) => void;
  onDeleted?: () => void;
}) {
  const { ability } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const asset = useAsset(id);
  const [tab, setTab] = useState<Tab>(initialTab);
  const [deleting, setDeleting] = useState(false);
  const a = asset.data;

  // Trace highlight only while the trace tab is open.
  useEffect(() => {
    if (tab !== 'trace') onTrace?.(null);
  }, [tab, onTrace]);
  useEffect(() => () => onTrace?.(null), [onTrace]);

  if (!a) {
    return (
      <Drawer label="Detail aset" title={asset.error ? 'Aset tidak tersedia' : 'Memuat…'} onClose={onClose}>
        {asset.error ? (
          <div className="fn-alert fn-alert--danger" role="alert">
            <div className="fn-alert__body">{asset.error.message}</div>
          </div>
        ) : (
          <span className="fn-spinner" aria-label="Memuat" />
        )}
      </Drawer>
    );
  }

  const target = subject('Asset', { areaCode: a.areaCode });
  const canEdit = !a.deletedAt && (ability.can('update', target) || ability.can('field_update', target));
  const canDelete = ability.can('delete', target);
  const tabs: Array<[Tab, string]> = [
    ['summary', 'Ringkasan'],
    ...(hasPorts(a.typeCode) ? ([['ports', 'Port']] as Array<[Tab, string]>) : []),
    ...(TRACE_TYPES.includes(a.typeCode) ? ([['trace', 'Trace']] as Array<[Tab, string]>) : []),
    ...(MONITORABLE_TYPES.includes(a.typeCode) &&
    ability.can('read', subject('Monitoring', { areaCode: a.areaCode }))
      ? ([['monitoring', 'Monitoring']] as Array<[Tab, string]>)
      : []),
    ['history', 'Riwayat'],
  ];

  async function restore() {
    try {
      await send('POST', `/api/v1/assets/${a!.id}/restore`);
      await invalidateAssets(qc);
      toast({ tone: 'success', text: `${a!.code} dipulihkan.` });
    } catch (err) {
      toast({ tone: 'danger', text: (err as Error).message });
    }
  }

  return (
    <Drawer
      label={`Detail ${a.name}`}
      eyebrow={assetTypeName(a.typeCode)}
      badge={
        <Badge tone={STATUS_TONE[a.status] ?? 'neutral'} dot>
          {ASSET_STATUS_LABELS[a.status as AssetStatus] ?? a.status}
        </Badge>
      }
      title={a.name}
      subtitle={a.code}
      icon={<AssetGlyph type={a.typeCode} />}
      onClose={onClose}
      actions={
        <>
          {onFocus && (
            <button
              type="button"
              className="fn-icon-btn fn-icon-btn--ghost fn-icon-btn--sm"
              aria-label="Fokus di peta"
              title="Fokus di peta"
              onClick={() => onFocus(a)}
            >
              <Crosshair className="i i--sm" aria-hidden />
            </button>
          )}
          {onStartRoute &&
            !a.deletedAt &&
            ability.can('create', subject('Route', { areaCode: a.areaCode })) && (
              <button
                type="button"
                className="fn-icon-btn fn-icon-btn--ghost fn-icon-btn--sm"
                aria-label="Buat jalur dari aset ini"
                title="Buat jalur dari sini"
                onClick={() => onStartRoute(a)}
              >
                <RouteIcon className="i i--sm" aria-hidden />
              </button>
            )}
          {onMove && canEdit && (
            <button
              type="button"
              className="fn-icon-btn fn-icon-btn--ghost fn-icon-btn--sm"
              aria-label="Pindahkan titik"
              title="Pindahkan titik"
              onClick={() => onMove(a)}
            >
              <Move className="i i--sm" aria-hidden />
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              className="fn-icon-btn fn-icon-btn--ghost fn-icon-btn--sm"
              aria-label="Edit aset"
              title="Edit"
              onClick={() => onEdit(a)}
            >
              <Pencil className="i i--sm" aria-hidden />
            </button>
          )}
          {canDelete && !a.deletedAt && (
            <button
              type="button"
              className="fn-icon-btn fn-icon-btn--ghost fn-icon-btn--danger fn-icon-btn--sm"
              aria-label="Hapus aset"
              title="Hapus"
              onClick={() => setDeleting(true)}
            >
              <Trash2 className="i i--sm" aria-hidden />
            </button>
          )}
        </>
      }
    >
      {a.deletedAt && (
        <div className="fn-alert fn-alert--warning" role="status">
          <div className="fn-alert__body">
            <span className="fn-alert__title">Di tempat sampah</span>
            <span className="fn-alert__text" style={{ color: 'inherit' }}>
              Dihapus {dateTime.format(new Date(a.deletedAt))}. Terhapus permanen 30 hari setelahnya.
            </span>
          </div>
          {canDelete && (
            <Button size="sm" icon={<RotateCcw className="i" aria-hidden />} onClick={() => void restore()}>
              Pulihkan
            </Button>
          )}
        </div>
      )}

      <div className="fn-tabs" role="tablist">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            className="fn-tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
          >
            {label}
            {key === 'ports' && a.ports.length > 0 && (
              <Badge square>
                {a.ports.reduce((n, p) => n + p.used, 0)}/{a.ports.reduce((n, p) => n + p.total, 0)}
              </Badge>
            )}
          </button>
        ))}
      </div>

      {tab === 'summary' && <Summary asset={a} onOpenAsset={onOpenAsset} />}
      {tab === 'ports' && <PortPanel assetId={a.id} areaCode={a.areaCode} onOpenAsset={onOpenAsset} />}
      {tab === 'trace' && <TracePanel assetId={a.id} onTrace={onTrace} onOpenAsset={onOpenAsset} />}
      {tab === 'monitoring' && <MonitoringPanel assetId={a.id} areaCode={a.areaCode} />}
      {tab === 'history' && <HistoryPanel asset={a} />}

      {deleting && (
        <DeleteAssetDialog
          asset={a}
          onClose={() => setDeleting(false)}
          onDeleted={() => {
            setDeleting(false);
            onDeleted?.();
          }}
        />
      )}
    </Drawer>
  );
}

function Summary({ asset: a, onOpenAsset }: { asset: AssetDetail; onOpenAsset: (id: string) => void }) {
  const fields = assetFields(a.typeCode);
  const needsSource = ['ODC', 'ODP', 'CPE'].includes(a.typeCode);
  return (
    <>
      {a.isSample && (
        <Badge tone="primary" square>
          Data contoh
        </Badge>
      )}
      {a.ports
        .filter((p) => p.type !== 'input')
        .map((p) => (
          <Capacity
            key={p.type}
            label={a.typeCode === 'ODP' ? 'Port pelanggan' : PORT_TYPE_LABELS[p.type as PortType]}
            used={p.used}
            reserved={p.reserved}
            total={p.total}
          />
        ))}
      {needsSource && !a.deletedAt && <UpstreamCard asset={a} onOpenAsset={onOpenAsset} />}

      <dl className="fn-dl">
        <dt>Kode</dt>
        <dd className="mono">{a.code}</dd>
        <dt>Wilayah</dt>
        <dd>{a.areaCode ? <RegionChip code={a.areaCode} /> : '—'}</dd>
        <dt>Koordinat</dt>
        <dd className="mono">
          {a.lat.toFixed(6)}, {a.lng.toFixed(6)}
        </dd>
        {a.address && (
          <>
            <dt>Alamat</dt>
            <dd>{a.address}</dd>
          </>
        )}
        {a.model && (
          <>
            <dt>Model</dt>
            <dd>{a.model}</dd>
          </>
        )}
        {a.price !== null && (
          <>
            <dt>Harga</dt>
            <dd className="num">{rupiah.format(a.price)}</dd>
          </>
        )}
        {fields.map((f) => {
          const v = a.details[f.key];
          if (v === null || v === undefined || v === '') return null;
          const shown =
            f.options?.find((o) => o.value === v)?.label ?? (typeof v === 'number' ? nf1.format(v) : v);
          return (
            <div key={f.key} style={{ display: 'contents' }}>
              <dt>{f.label}</dt>
              <dd className={f.input === 'ip' || f.input === 'number' ? 'mono' : undefined}>
                {shown}
                {f.unit ? ` ${f.unit}` : ''}
              </dd>
            </div>
          );
        })}
      </dl>
      {a.description && (
        <p className="fn-card__desc" style={{ whiteSpace: 'pre-wrap' }}>
          {a.description}
        </p>
      )}
      <p className="fn-hint">
        Dibuat {dateTime.format(new Date(a.createdAt))}
        {a.createdBy ? ` oleh ${a.createdBy}` : ''} · diubah {dateTime.format(new Date(a.updatedAt))}
        {a.updatedBy ? ` oleh ${a.updatedBy}` : ''}
      </p>
    </>
  );
}

function Capacity({
  label,
  used,
  reserved,
  total,
}: {
  label: string;
  used: number;
  reserved: number;
  total: number;
}) {
  const pct = total > 0 ? (used / total) * 100 : 0;
  return (
    <div
      className={`fn-capacity${pct >= 100 ? ' fn-capacity--full' : pct >= 80 ? ' fn-capacity--high' : ''}`}
    >
      <div className="fn-capacity__legend">
        <span>{label}</span>
        <span>
          <strong>{nf.format(used)}</strong>/{nf.format(total)} terpakai
          {reserved > 0 ? ` · ${reserved} dipesan` : ''}
        </span>
      </div>
      <div className="fn-capacity__track">
        <span className="fn-capacity__used" style={{ width: `${pct}%` }} />
        <span
          className="fn-capacity__reserved"
          style={{ width: `${total > 0 ? (reserved / total) * 100 : 0}%` }}
        />
      </div>
    </div>
  );
}

/** "Where does the signal come from": the link on the input port(s), or a shortcut to connect. */
function UpstreamCard({ asset, onOpenAsset }: { asset: AssetDetail; onOpenAsset: (id: string) => void }) {
  const { ability } = useSession();
  const ports = usePorts(asset.id);
  const [connect, setConnect] = useState<PortView | null>(null);
  const inputs = (ports.data ?? []).filter((p) => p.type === 'input');
  if (ports.isPending || inputs.length === 0) return null;
  const linked = inputs.filter((p) => p.link);
  const free = inputs.find((p) => !p.link && p.status !== 'maintenance');
  const canField = ability.can('field_update', subject('Asset', { areaCode: asset.areaCode }));

  return (
    <section className="fn-card" style={{ background: 'var(--bg-surface-inset)' }}>
      <div className="fn-card__body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span className="fn-label">Sumber sinyal</span>
        {linked.map((p) => (
          <button
            key={p.id}
            type="button"
            className="fn-layer"
            style={{ padding: 0 }}
            disabled={p.link!.peer.restricted}
            onClick={() => onOpenAsset(p.link!.peer.assetId)}
          >
            <span className="fn-layer__glyph">
              <AssetGlyph type={p.link!.peer.typeCode} />
            </span>
            <span className="fn-layer__name">
              {p.link!.peer.name}
              <span className="mono" style={{ display: 'block', color: 'var(--text-muted)', fontSize: 11 }}>
                {p.link!.peer.code} · {p.link!.peer.portName} → {p.name}
              </span>
            </span>
            {p.link!.lengthM != null && <span className="fn-layer__count">{metres(p.link!.lengthM)}</span>}
          </button>
        ))}
        {linked.length === 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
            <span
              className="fn-hint"
              style={{ color: 'var(--warning-text)', display: 'inline-flex', gap: 6, alignItems: 'center' }}
            >
              <TriangleAlert className="i i--sm" aria-hidden /> Belum terhubung ke{' '}
              {asset.typeCode === 'CPE' ? 'ODP' : 'upstream'}
            </span>
            {canField && free && (
              <Button
                size="sm"
                variant="primary"
                icon={<Link2 className="i" aria-hidden />}
                onClick={() => setConnect(free)}
              >
                Hubungkan…
              </Button>
            )}
          </div>
        )}
      </div>
      {connect && <ConnectDialog port={connect} onClose={() => setConnect(null)} />}
    </section>
  );
}

function TracePanel({
  assetId,
  onTrace,
  onOpenAsset,
}: {
  assetId: string;
  onTrace?: (c: Array<[number, number]> | null) => void;
  onOpenAsset: (id: string) => void;
}) {
  const trace = useTrace(assetId);
  useEffect(() => {
    if (trace.data) onTrace?.(trace.data.steps.map((s) => [s.lng, s.lat]));
  }, [trace.data, onTrace]);

  if (trace.isPending) return <span className="fn-spinner" aria-label="Memuat trace" />;
  if (trace.error)
    return (
      <div className="fn-alert fn-alert--danger">
        <div className="fn-alert__body">{trace.error.message}</div>
      </div>
    );
  const t = trace.data;
  const steps = [...t.steps].reverse(); // OLT first, following the signal
  const last = t.steps[t.steps.length - 1];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {!t.complete && (
        <div className="fn-alert fn-alert--warning" role="status">
          <div className="fn-alert__body">
            <span className="fn-alert__title">Jalur belum sampai OLT</span>
            <span className="fn-alert__text" style={{ color: 'inherit' }}>
              {last?.code} belum terhubung ke upstream. Hubungkan port inputnya dari tab Port.
            </span>
          </div>
        </div>
      )}
      <div className="fn-trace">
        {steps.map((s, i) => {
          const child = steps[i + 1];
          return (
            <div key={s.assetId} className="fn-trace__step">
              <span className="fn-trace__node">
                <AssetGlyph type={s.typeCode} />
              </span>
              <button
                type="button"
                disabled={s.restricted}
                onClick={() => onOpenAsset(s.assetId)}
                style={{
                  background: 'none',
                  border: 0,
                  padding: 0,
                  textAlign: 'left',
                  cursor: s.restricted ? 'default' : 'pointer',
                  color: 'inherit',
                  minWidth: 0,
                }}
              >
                <div className="fn-trace__name">{s.name}</div>
                <div className="fn-trace__meta">
                  {s.code}
                  {child?.upstreamPort &&
                    ` · ${child.upstreamPort}${child.vlanId ? ` (VLAN ${child.vlanId})` : ''} → ${child.inPort}`}
                  {child?.linkLengthM != null && ` · ${metres(child.linkLengthM)}`}
                </div>
              </button>
              <span className="fn-trace__loss">
                {s.splitterRatio
                  ? `${s.splitterRatio} · ${nf1.format(s.splitterLossDb ?? 0)} dB`
                  : s.areaCode
                    ? areaName(s.areaCode)
                    : ''}
              </span>
            </div>
          );
        })}
      </div>
      <div className="fn-trace__total">
        <span>Estimasi redaman OLT → titik ini</span>
        <span className="num">
          {t.estimatedLossDb === null ? '—' : `${nf1.format(t.estimatedLossDb)} dB`}
        </span>
      </div>
      {t.downstreamCustomers > 0 && (
        <p className="fn-hint">
          {nf.format(t.downstreamCustomers)} pelanggan mendapat sinyal lewat aset ini.
        </p>
      )}
      <p className="fn-hint">
        Estimasi = redaman splitter + hasil ukur per link (atau 0,35 dB/km bila belum diukur). Jalur disorot
        di peta.
      </p>
    </div>
  );
}

const ACTION_LABEL: Record<string, string> = {
  'asset.create': 'Dibuat',
  'asset.update': 'Diubah',
  'asset.move': 'Dipindah',
  'asset.delete': 'Dihapus',
  'asset.restore': 'Dipulihkan',
  'port.update': 'Port diubah',
  'port.link': 'Port dihubungkan',
  'port.unlink': 'Koneksi diputus',
};

const COMMON_LABEL: Record<string, string> = {
  code: 'Kode',
  name: 'Nama',
  status: 'Status',
  model: 'Model',
  description: 'Deskripsi',
  address: 'Alamat',
  price: 'Harga',
  areaCode: 'Wilayah',
  lat: 'Lintang',
  lng: 'Bujur',
};

function HistoryPanel({ asset }: { asset: AssetDetail }) {
  const history = useHistory(asset.id);
  if (history.isPending) return <span className="fn-spinner" aria-label="Memuat riwayat" />;
  if (history.error)
    return (
      <div className="fn-alert fn-alert--danger">
        <div className="fn-alert__body">{history.error.message}</div>
      </div>
    );
  if (history.data.length === 0) return <p className="fn-hint">Belum ada riwayat perubahan.</p>;
  const labelOf = (key: string) =>
    COMMON_LABEL[key] ?? assetFields(asset.typeCode).find((f) => `details.${f.key}` === key)?.label ?? key;

  return (
    <div className="fn-feed">
      {history.data.map((h) => (
        <div key={h.id} className="fn-feed__item" style={{ paddingInline: 0 }}>
          <span className="fn-status fn-status--unknown" style={{ paddingTop: 5 }}>
            <span className="fn-status__dot" />
          </span>
          <div style={{ minWidth: 0 }}>
            <div className="fn-feed__title">
              {ACTION_LABEL[h.action] ?? h.action}
              {h.user && <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}> · {h.user}</span>}
            </div>
            <div className="fn-feed__meta">{describe(h, labelOf)}</div>
          </div>
          <div className="fn-feed__time">{dateTime.format(new Date(h.at))}</div>
        </div>
      ))}
    </div>
  );
}

function describe(h: AssetHistoryEntry, labelOf: (k: string) => string): string {
  const d = (h.diff ?? {}) as {
    changes?: Record<string, [unknown, unknown]>;
    from?: string;
    to?: string;
    port?: string;
    linksRemoved?: number;
  };
  if (d.changes) {
    return Object.entries(d.changes)
      .map(([k, [from, to]]) => `${labelOf(k)}: ${show(k, from)} → ${show(k, to)}`)
      .join(' · ');
  }
  if (d.from && d.to) return `${d.from} → ${d.to}`;
  if (d.port) return d.port;
  if (d.linksRemoved) return `${d.linksRemoved} koneksi ikut dihapus`;
  return '';
}

function show(key: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (key === 'status') return ASSET_STATUS_LABELS[v as AssetStatus] ?? String(v);
  if (key === 'areaCode') return areaName(String(v));
  return String(v);
}
